import { isKiosk, kioskRoot } from 'src/config'
import Model from 'src/models/model'
import RegisterClient from 'src/models/register/RegisterClient'
import { updateGlobalActivityIdHeader, updateGlobalShowingIdHeader } from 'src/modules/checkout/guards'

export default ({ router, app }) => {
  if (!isKiosk) return

  async function quitRegister () {
    if (Model.context.currentRegisterClient) {
      await Model.context.currentRegisterClient.delete()
    }
    if (Model.context.currentUser) {
      await Model.context.currentUser.logOut()
    }
    await Model.configuration.validate()
    await Model.context.fetch(true)
    router.replace({ name: 'auth:login' })
  }
  app.config.globalProperties.$quitRegister = quitRegister
  app.$quitRegister = quitRegister

  /**
   * Set routes
   */
  const routeData = {
    path: kioskRoot,
    component: () => import('src/modules/kiosk/layouts/KioskLayout'),
    beforeEnter: async (_to, _from, next) => {
      await Model.configuration.ensureLoaded()

      if (!Model.context.currentRegisterClient) {
        // If the user is logged in we need to create the current register client
        Model.context.currentRegisterClient = new RegisterClient({}, { registerStation: true })
      }

      // If there is no register client and not logged in, force login
      // If there are either of those, handle from the register layout screen
      if (!Model.context.currentUser) {
        next({ name: 'auth:login' })
        return
      }

      next()
    }
  }

  routeData.children = [
    {
      name: 'kiosk:idle',
      path: '',
      component: () => import('src/modules/kiosk/components/WelcomeScreen')
    },
    // Create duplicate route to have a convenience "root" route name
    {
      name: 'kiosk:root',
      path: '',
      component: () => import('src/modules/kiosk/components/WelcomeScreen')
    },
    {
      name: 'kiosk:action-selection',
      path: 'action-selection',
      component: () => import('src/modules/kiosk/components/ActionSelectionPage')
    },
    {
      name: 'kiosk:showings',
      path: 'showings',
      component: () => import('src/modules/kiosk/components/ShowingSelectionPage')
    },
    {
      name: 'kiosk:activities',
      path: 'activities',
      component: () => import('src/modules/register/components/activities/ActivitiesGrid')
    },
    {
      name: 'kiosk:showing',
      path: 'checkout/showing/:id',
      component: () => import('src/modules/checkout/pages/Showing'),
      beforeEnter: updateGlobalShowingIdHeader
    },
    {
      name: 'kiosk:showing-slug',
      path: 'checkout/showing/:slug/:id',
      component: () => import('src/modules/checkout/pages/Showing'),
      beforeEnter: updateGlobalShowingIdHeader
    },
    {
      name: 'kiosk:activity',
      path: 'checkout/activity/:slug',
      component: () => import('src/modules/checkout/pages/ActivityCheckout'),
      beforeEnter: updateGlobalActivityIdHeader
    },
    {
      name: 'kiosk:activity-date-time',
      path: 'checkout/activity/:slug/date-and-time',
      component: () => import('src/modules/checkout/pages/ActivityCheckout'),
      beforeEnter: updateGlobalActivityIdHeader
    },
    {
      name: 'kiosk:activity-add-ons',
      path: 'checkout/activity/:slug/add-ons',
      component: () => import('src/modules/checkout/pages/ActivityCheckout'),
      beforeEnter: updateGlobalActivityIdHeader
    },
    {
      name: 'kiosk:more-activities',
      path: 'checkout/more-activities',
      component: () => import('src/modules/checkout/pages/MoreActivities')
    },
    {
      name: 'kiosk:movie',
      path: 'movie/:slug',
      component: () => import('src/modules/consumer/pages/MoviePage')
    },
    {
      name: 'kiosk:seats',
      path: 'checkout/seats/:showing',
      component: () => import('src/modules/checkout/pages/Seats'),
      beforeEnter: updateGlobalShowingIdHeader
    },
    {
      name: 'kiosk:seat-chart',
      path: 'checkout/seat-chart',
      component: () => import('src/modules/checkout/pages/Seats')
    },
    {
      name: 'kiosk:cart',
      path: 'checkout/cart',
      component: () => import('src/modules/kiosk/components/KioskCart')
    },
    {
      name: 'kiosk:payment',
      path: 'checkout/payment',
      component: () => import('src/modules/kiosk/components/KioskPaymentPage')
    },
    {
      name: 'kiosk:confirmation',
      path: 'confirmation',
      component: () => import('src/modules/kiosk/components/OrderConfirmationPage')
    },
    {
      name: 'kiosk:find-user',
      path: 'find-user',
      component: () => import('src/modules/kiosk/components/KioskLoginPage')
    },
    {
      name: 'kiosk:register-user',
      path: 'register-user',
      component: () => import('src/modules/kiosk/components/KioskRegisterPage')
    },
    {
      name: 'kiosk:membership-action',
      path: 'membership-action',
      component: () => import('src/modules/kiosk/components/MembershipManagementSelection')
    },
    {
      name: 'kiosk:pickup',
      path: 'pickup',
      component: () => import('src/modules/kiosk/components/OrderCollectionPage')
    },
    {
      name: 'kiosk:user-orders',
      path: 'user-orders',
      component: () => import('src/modules/kiosk/components/UserOrdersPage')
    },
    {
      name: 'kiosk:user-actions',
      path: 'user-actions',
      component: () => import('src/modules/kiosk/components/UserLoginActionPage')
    },
    {
      name: 'kiosk:items',
      path: 'checkout/items',
      component: () => import('src/modules/checkout/pages/Items')
    },
    {
      name: 'kiosk:ticket-package-options',
      path: 'checkout/ticket-package-options',
      component: () => import('src/modules/checkout/pages/TicketPackageOptions')
    },

    {
      name: 'kiosk:delivery-selection',
      path: 'checkout/delivery-selection',
      component: () => import('src/modules/kiosk/pages/DeliverySelectionPage')
    }
  ]

  router.addRoute(routeData)

  if (isKiosk) {
    router.addRoute({ name: 'app:root', redirect: { name: 'kiosk:root' } })
  }
}
